import React, { ComponentProps, SyntheticEvent, useRef, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, MenuItem, Select, styled } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { SelectInputProps } from '@mui/material/Select/SelectInput'

import { IconButton } from '../IconButton/IconButton'
import { TextField } from '../TextField/TextField'
import { SearchResult } from './models/SearchResult'
import { SearchType, SearchTypeEnum } from './models/SearchType'

export type SearchBarProps = Partial<ComponentProps<typeof Autocomplete>> & {
  searchTypes: SearchType[]
  defaultSearchType: SearchType
  onSearch: (query: string, type: SearchType) => void
  options: SearchResult[]
  placeholder?: string
  loading?: boolean
  slotProps?: {
    textField?: React.ComponentProps<typeof TextField>
    iconButton?: React.ComponentProps<typeof IconButton>
    select?: React.ComponentProps<typeof Select>
  }
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  options,
  placeholder = 'Search Order...',
  slotProps = {},
  searchTypes,
  loading,
  defaultSearchType = 'orderNumber',
  ...props
}) => {
  const [query, setQuery] = useState<string>('')
  const [searchType, setSearchType] = useState<SearchType>(defaultSearchType)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState<boolean>(false)
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleSearch = () => {
    if (!query.trim()) {
      return
    }

    onSearch(query.trim(), searchType)
    setIsAutocompleteOpen(true)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearch()
    }
  }

  const handleSelectChange: SelectInputProps['onChange'] = (event) => {
    setSearchType(event.target.value as SearchType)
  }

  const handleSelectOpen = (event: SyntheticEvent<Element, Event>) => {
    event.stopPropagation()
    setIsSelectOpen(true)
    setIsAutocompleteOpen(false)
  }

  const handleSelectClose = () => {
    setIsSelectOpen(false)
  }

  const handleFocus = () => {
    setIsFocused(true)

    if (!isSelectOpen) {
      setTimeout(() => {
        setIsAutocompleteOpen(true)
      }, 300)
    }
  }

  const handleBlur = (event: React.FocusEvent) => {
    // Only blur if we're not clicking within the component
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsAutocompleteOpen(false)
      setIsFocused(false)
    }
  }

  return (
    <StyledContainer onFocus={handleFocus} onBlur={handleBlur}>
      <StyledAutocomplete
        {...props}
        freeSolo
        open={isAutocompleteOpen && !isSelectOpen}
        options={(options && options.length === 0) ? [{ orderId: 'no-results', orderNumber: '' }] : options}
        loading={loading}
        inputValue={query}
        onInputChange={(_, value) => setQuery(value)}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option
          }

          if ((option as SearchResult).orderId === 'no-results') {
            return ''
          }

          return (option as SearchResult).orderNumber
        }}
        filterOptions={(opts, { inputValue }) => {
          const searchText = inputValue.toLowerCase()

          if (!opts.length || opts.length === 0) {
            return []
          }

          return (opts as SearchResult[]).filter(option => {
            if (searchType === 'customerName') {
              return option.customerName?.toLowerCase().includes(searchText)
            } else if (searchType === 'orderNumber') {
              return option.orderNumber?.toLowerCase().includes(searchText)
            } else if (searchType === 'customerNumber') {
              return option.customerNumber?.toLowerCase().includes(searchText)
            }

            return true
          })
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            round
            $isFocused={isFocused}
            placeholder={placeholder}
            onKeyDown={handleKeyPress}
            inputRef={inputRef}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <StyledAdornmentContainer>
                      <StyledIconButton
                        onClick={handleSearch}
                        disabled={!isFocused}
                        $isFocused={isFocused}
                        size="small"
                        {...slotProps.iconButton}
                      >
                        <SearchIcon />
                      </StyledIconButton>
                      <StyledDivider $isFocused={isFocused} />
                      <StyledSelect
                        value={searchType}
                        disabled={!isFocused}
                        onChange={handleSelectChange}
                        variant="standard"
                        open={isSelectOpen}
                        onOpen={handleSelectOpen}
                        onClose={handleSelectClose}
                        MenuProps={{
                          disableScrollLock: true,
                          onClick: (e) => e.stopPropagation(),
                        }}
                        {...slotProps.select}
                      >
                        {searchTypes.map((type) => (
                          <MenuItem
                            key={type}
                            value={type}
                            disableRipple
                            onClick={(e) => e.stopPropagation()}
                          >
                            {SearchTypeEnum[type]}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledAdornmentContainer>
                  </InputAdornment>
                ),
              },
            }}
            {...slotProps.textField}
          />
        )}
      />
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  width: 100%;
  position: relative;
  display: inline-block;
  margin-left: auto;
`

const StyledAutocomplete = styled(Autocomplete)`
  &.MuiAutocomplete-root > .MuiFormControl-root.MuiFormControl-marginNormal {
    margin-bottom: 0;
    margin-top: 0;
  }
`

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== '$isFocused',
}) <{ $isFocused: boolean }>`
  & .MuiOutlinedInput-root {
    margin-left: auto;
    width: 100%;
    max-width: ${pxToRem(480)};
    transition: width 0.15s ease-in-out;
    background-color: ${({ theme }) => theme.palette.background.paper};

    ${props => props.theme.breakpoints.up('lg')} {
      width: ${({ $isFocused }) => ($isFocused ? '100%' : pxToRem(320))};
      min-width: ${pxToRem(320)};
    }
  }

  & .MuiInputBase-root {
    padding-right: 0 !important;
  }
`

const StyledAdornmentContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(1)};
`

const StyledSelect = styled(Select)`
  &.MuiInputBase-root {
    &.MuiButtonBase-root {
      border-radius: 100px;
    }

    &:before, &:after {
      display: none;
    }
  }

  & .MuiSelect-select {
    border: none;
    background: transparent;
    border-radius: 1000px;
    color: ${({ theme }) => theme.palette.primary.main};
    padding-left: ${pxToRem(8)};
  }

  & .MuiSelect-icon {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`

const StyledDivider = styled('div') <{ $isFocused: boolean }>`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.palette.divider};
  margin: 0 ${pxToRem(8)};
`

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== '$isFocused',
}) <{ $isFocused: boolean }>`
  margin: 0;
  color: ${({ theme, $isFocused }) => $isFocused ? theme.palette.primary.main : theme.palette.text.secondary};
  padding: ${({ theme }) => theme.spacing(0.5)};
  
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`